import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Link2,
  Plus,
  Trash2,
  Edit3,
  CheckCircle2,
  AlertTriangle,
  PauseCircle,
  Activity,
  Globe,
  Webhook,
  FileSpreadsheet,
  Plug,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  X,
  History,
  Users,
  Upload,
} from 'lucide-react';
import { ImportWizard } from './connect/ImportWizard';
import { ImportHistorySection } from './connect/ImportHistorySection';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { useAuth } from '@/lib/auth/useAuth';
import { PageSpinner, PageHeader, ProductIntro } from '@/components/ui';
import type { ResponseEnvelope } from '@/types/api';

interface ConnectOverview {
  totalIntegrations: number;
  activeIntegrations: number;
  pausedIntegrations: number;
  erroredIntegrations: number;
  totalEventsSynced: number;
  successfulSyncs: number;
  failedSyncs: number;
}

interface ConnectIntegration {
  id: string;
  displayName: string;
  integrationType: string;
  status: string;
  endpointUrl: string | null;
  httpMethod: string | null;
  apiKeyHint: string | null;
  triggerEvent: string;
  fieldMappings: Record<string, string> | null;
  batchSize: number;
  matchedOnly: boolean;
  lastSyncAt: string | null;
  totalEventsSynced: number;
  consecutiveErrors: number;
  lastErrorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConnectSyncEvent {
  id: string;
  integrationId: string;
  outcome: string;
  httpStatusCode: number | null;
  recordsAttempted: number;
  recordsDelivered: number;
  recordsFailed: number;
  durationMs: number | null;
  errorMessage: string | null;
  triggerType: string | null;
  createdAt: string;
}

interface SyncEventPage {
  content: ConnectSyncEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface CustomerIdentityProfile {
  profileId: string;
  mobileNumberMasked: string;
  operator: string | null;
  countryCode: string | null;
  totalVerifications: number;
  successfulVerifications: number;
  failedVerifications: number;
  lastVerificationOutcome: string | null;
  lastVerifiedAt: string | null;
  firstSeenAt: string;
  status: string;
}

interface ProfilePage {
  content: CustomerIdentityProfile[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface StuckPlusOverview {
  stuckCount: number;
  stuckPlusCount: number;
  avgAttemptsBeforeRecovery: number;
  totalSessions: number;
}

const DARK_GLASS = {
  background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
  border: '1px solid rgba(96,165,250,0.1)',
} as const;

const INTEGRATION_ICONS: Record<string, React.ReactNode> = {
  CSV_EXPORT:  <FileSpreadsheet className="h-5 w-5" />,
  WEBHOOK:     <Webhook className="h-5 w-5" />,
  CUSTOM_API:  <Plug className="h-5 w-5" />,
  SALESFORCE:  <Globe className="h-5 w-5" />,
  HUBSPOT:     <Globe className="h-5 w-5" />,
  ZOHO:        <Globe className="h-5 w-5" />,
  DYNAMICS:    <Globe className="h-5 w-5" />,
  SHOPIFY:     <Globe className="h-5 w-5" />,
  ZENDESK:     <Globe className="h-5 w-5" />,
};

const INTEGRATION_COLORS: Record<string, string> = {
  CSV_EXPORT:  '#34d399',
  WEBHOOK:     '#60a5fa',
  CUSTOM_API:  '#a78bfa',
  SALESFORCE:  '#00a1e0',
  HUBSPOT:     '#ff7a59',
  ZOHO:        '#e42527',
  DYNAMICS:    '#00a4ef',
  SHOPIFY:     '#96bf48',
  ZENDESK:     '#03363d',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  ACTIVE:  { label: 'Active',  color: '#34d399', icon: <CheckCircle2 className="h-4 w-4" /> },
  PAUSED:  { label: 'Paused',  color: '#fb923c', icon: <PauseCircle className="h-4 w-4" /> },
  ERRORED: { label: 'Errored', color: '#f43f5e', icon: <AlertTriangle className="h-4 w-4" /> },
};

const SYNC_OUTCOME_COLOR: Record<string, string> = {
  SUCCESS: '#34d399',
  PARTIAL: '#fb923c',
  FAILED:  '#f43f5e',
};

const INTEGRATION_TYPES = [
  { value: 'CSV_EXPORT',  label: 'CSV Export',   description: 'Periodic file export of verification events' },
  { value: 'WEBHOOK',     label: 'Webhook',       description: 'Real-time HTTP POST to your endpoint' },
  { value: 'CUSTOM_API',  label: 'Custom API',    description: 'Push data to any REST API endpoint' },
  { value: 'SALESFORCE',  label: 'Salesforce',    description: 'Coming soon', disabled: true },
  { value: 'HUBSPOT',     label: 'HubSpot',       description: 'Coming soon', disabled: true },
  { value: 'ZOHO',        label: 'Zoho CRM',      description: 'Coming soon', disabled: true },
];

const TRIGGER_EVENTS = [
  { value: 'VERIFICATION_COMPLETED', label: 'Verification Completed' },
  { value: 'VERIFICATION_FAILED',    label: 'Verification Failed' },
  { value: 'STUCK_RECOVERED',        label: 'Stuck+ Recovery Event' },
  { value: 'ALL',                    label: 'All Events' },
];

function useConnectOverview() {
  return useQuery({
    queryKey: ['connect-overview'],
    queryFn: async () => unwrap(await api.get<ResponseEnvelope<ConnectOverview>>(Endpoints.me.connect.overview)),
    staleTime: 30_000,
  });
}

function useConnectIntegrations() {
  return useQuery({
    queryKey: ['connect-integrations'],
    queryFn: async () => unwrap(await api.get<ResponseEnvelope<ConnectIntegration[]>>(Endpoints.me.connect.integrations)),
    staleTime: 20_000,
  });
}

function useSyncHistory(integrationId: string, enabled: boolean, page: number) {
  return useQuery({
    queryKey: ['sync-history', integrationId, page],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<SyncEventPage>>(
        Endpoints.me.connect.syncHistory(integrationId),
        { params: { page, size: 10 } }
      )),
    enabled,
    staleTime: 30_000,
  });
}

function useCustomerProfiles(page: number) {
  return useQuery({
    queryKey: ['customer-profiles', page],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<ProfilePage>>(
        Endpoints.me.connect.profiles,
        { params: { page, size: 25 } }
      )),
    staleTime: 30_000,
  });
}

interface IntegrationFormData {
  displayName: string;
  integrationType: string;
  endpointUrl: string;
  httpMethod: string;
  apiKey: string;
  triggerEvent: string;
  batchSize: number;
  matchedOnly: boolean;
  fieldMappings: Record<string, string>;
}

function AddMappingRow({ onAdd }: { onAdd: (key: string, value: string) => void }) {
  const [key, setKey] = useState('');
  const [val, setVal] = useState('');
  return (
    <div className="flex items-center gap-2">
      <input
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Source field"
        className="field-input flex-1"
      />
      <span className="shrink-0 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Target field"
        className="field-input flex-1"
      />
      <button
        disabled={!key.trim() || !val.trim()}
        onClick={() => { onAdd(key.trim(), val.trim()); setKey(''); setVal(''); }}
        className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-40"
        style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }}
      >
        Add
      </button>
    </div>
  );
}

function IntegrationFormModal({
  existing,
  onClose,
  onSaved,
}: {
  existing?: ConnectIntegration;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<IntegrationFormData>({
    displayName: existing?.displayName ?? '',
    integrationType: existing?.integrationType ?? 'WEBHOOK',
    endpointUrl: existing?.endpointUrl ?? '',
    httpMethod: existing?.httpMethod ?? 'POST',
    apiKey: '',
    triggerEvent: existing?.triggerEvent ?? 'VERIFICATION_COMPLETED',
    batchSize: existing?.batchSize ?? 100,
    matchedOnly: existing?.matchedOnly ?? false,
    fieldMappings: existing?.fieldMappings ?? {},
  });
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: async () =>
      unwrap(await api.post<ResponseEnvelope<ConnectIntegration>>(Endpoints.me.connect.integrations, {
        displayName: form.displayName,
        integrationType: form.integrationType,
        endpointUrl: form.endpointUrl || null,
        httpMethod: form.httpMethod,
        apiKey: form.apiKey || null,
        triggerEvent: form.triggerEvent,
        fieldMappings: Object.keys(form.fieldMappings).length > 0 ? form.fieldMappings : null,
        batchSize: form.batchSize,
        matchedOnly: form.matchedOnly,
      })),
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: async () =>
      unwrap(await api.patch<ResponseEnvelope<ConnectIntegration>>(
        Endpoints.me.connect.integration(existing!.id),
        {
          displayName: form.displayName || null,
          endpointUrl: form.endpointUrl || null,
          httpMethod: form.httpMethod || null,
          apiKey: form.apiKey || null,
          triggerEvent: form.triggerEvent || null,
          fieldMappings: Object.keys(form.fieldMappings).length > 0 ? form.fieldMappings : null,
          batchSize: form.batchSize || null,
          matchedOnly: form.matchedOnly,
        }
      )),
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const requiresEndpoint = ['WEBHOOK', 'CUSTOM_API'].includes(form.integrationType);

  const removeMapping = (key: string) =>
    setForm((f) => {
      const m = { ...f.fieldMappings };
      delete m[key];
      return { ...f, fieldMappings: m };
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ ...DARK_GLASS, boxShadow: '0 0 40px rgba(96,165,250,0.12), 0 24px 48px rgba(0,0,0,0.6)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">
            {existing ? 'Edit Integration' : 'New Integration'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 transition-colors hover:bg-white/10">
            <X className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Type selector — only for new integrations */}
          {!existing && (
            <div>
              <label className="mb-2 block text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Integration Type
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {INTEGRATION_TYPES.map((t) => (
                  <button
                    key={t.value}
                    disabled={t.disabled}
                    onClick={() => setForm((f) => ({ ...f, integrationType: t.value }))}
                    className={`flex flex-col items-start rounded-xl p-3 text-left transition-all ${t.disabled ? 'opacity-35' : ''}`}
                    style={{
                      background: form.integrationType === t.value ? `${INTEGRATION_COLORS[t.value] ?? '#60a5fa'}18` : 'rgba(255,255,255,0.03)',
                      border: form.integrationType === t.value ? `1px solid ${INTEGRATION_COLORS[t.value] ?? '#60a5fa'}50` : '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    <span style={{ color: form.integrationType === t.value ? (INTEGRATION_COLORS[t.value] ?? '#60a5fa') : 'rgba(255,255,255,0.4)' }}>
                      {INTEGRATION_ICONS[t.value]}
                    </span>
                    <span className="mt-1.5 text-xs font-semibold text-white">{t.label}</span>
                    <span className="mt-0.5 text-[10px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.3)' }}>{t.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <Field label="Display Name" required>
            <input
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="e.g. Production Webhook"
              className="field-input"
            />
          </Field>

          {requiresEndpoint && (
            <>
              <Field label="Endpoint URL">
                <input
                  value={form.endpointUrl}
                  onChange={(e) => setForm((f) => ({ ...f, endpointUrl: e.target.value }))}
                  placeholder="https://your-crm.com/webhooks/authmatech"
                  className="field-input"
                />
              </Field>
              <Field label="HTTP Method">
                <select
                  value={form.httpMethod}
                  onChange={(e) => setForm((f) => ({ ...f, httpMethod: e.target.value }))}
                  className="field-input"
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </select>
              </Field>
            </>
          )}

          <Field label={existing ? 'New API Key (leave blank to keep existing)' : 'API Key / Bearer Token'}>
            <input
              type="password"
              value={form.apiKey}
              onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
              placeholder={existing ? '••••••••' : 'Bearer token or API key'}
              className="field-input"
              autoComplete="off"
            />
            <p className="mt-1 text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Stored encrypted. Never returned after save.
            </p>
          </Field>

          <Field label="Trigger Event">
            <select
              value={form.triggerEvent}
              onChange={(e) => setForm((f) => ({ ...f, triggerEvent: e.target.value }))}
              className="field-input"
            >
              {TRIGGER_EVENTS.map((ev) => (
                <option key={ev.value} value={ev.value}>{ev.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Batch Size">
              <input
                type="number"
                min={1}
                max={1000}
                value={form.batchSize}
                onChange={(e) => setForm((f) => ({ ...f, batchSize: Number(e.target.value) }))}
                className="field-input"
              />
            </Field>
            <Field label="">
              <label className="flex cursor-pointer items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={form.matchedOnly}
                  onChange={(e) => setForm((f) => ({ ...f, matchedOnly: e.target.checked }))}
                  className="h-4 w-4 accent-blue-400"
                />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Matched verifications only</span>
              </label>
            </Field>
          </div>

          {/* Field Mappings */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Field Mappings{' '}
                <span className="font-normal" style={{ color: 'rgba(255,255,255,0.3)' }}>(optional)</span>
              </label>
              {Object.keys(form.fieldMappings).length > 0 && (
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {Object.keys(form.fieldMappings).length} mapped
                </span>
              )}
            </div>

            {Object.entries(form.fieldMappings).length > 0 && (
              <div className="mb-2 flex flex-col gap-1.5 rounded-xl p-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                {Object.entries(form.fieldMappings).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="flex-1 rounded-lg px-2 py-1.5 font-mono text-xs text-white" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      {k}
                    </span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>→</span>
                    <span className="flex-1 rounded-lg px-2 py-1.5 font-mono text-xs text-white" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      {v}
                    </span>
                    <button
                      onClick={() => removeMapping(k)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-red-500/20"
                    >
                      <X className="h-3 w-3" style={{ color: '#f43f5e' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <AddMappingRow
              onAdd={(k, v) => setForm((f) => ({ ...f, fieldMappings: { ...f.fieldMappings, [k]: v } }))}
            />
            <p className="mt-1.5 text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Map Authmatech fields (e.g. <span className="font-mono">operator</span>) to your CRM target fields.
            </p>
          </div>

          {/* Privacy reminder */}
          <div className="flex items-start gap-2 rounded-xl px-3 py-2.5" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: '#60a5fa' }} />
            <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Mobile numbers synced through Connect are always masked (<span className="font-mono font-bold text-white">************</span>). Full reveal is only available via Stuck+ Secure Reveal.
            </p>
          </div>

          {error && (
            <div className="rounded-lg px-3 py-2 text-xs text-red-400" style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.2)' }}>
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            >
              Cancel
            </button>
            <button
              onClick={() => existing ? updateMutation.mutate() : createMutation.mutate()}
              disabled={!form.displayName || isPending}
              className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
            >
              {isPending ? 'Saving…' : existing ? 'Save Changes' : 'Create Integration'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .field-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 9px 12px;
          font-size: 13px;
          color: white;
          outline: none;
          transition: border-color 0.15s;
        }
        .field-input:focus { border-color: rgba(96,165,250,0.4); }
        .field-input option { background: #050e1d; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      {label && (
        <label className="mb-1.5 block text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {label} {required && <span className="text-red-400">*</span>}
        </label>
      )}
      {children}
    </div>
  );
}

function SyncHistorySection({ integrationId }: { integrationId: string }) {
  const [page, setPage] = useState(0);
  const history = useSyncHistory(integrationId, true, page);

  if (history.isLoading) {
    return (
      <div className="flex h-20 items-center justify-center">
        <Activity className="h-4 w-4 animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} />
      </div>
    );
  }

  if (!history.data?.content.length) {
    return (
      <p className="py-4 text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
        No sync events recorded yet.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-1.5">
        {history.data.content.map((ev) => {
          const color = SYNC_OUTCOME_COLOR[ev.outcome] ?? '#64748b';
          return (
            <div
              key={ev.id}
              className="flex flex-wrap items-center gap-3 rounded-lg px-3 py-2"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                style={{ background: `${color}18`, color }}
              >
                {ev.outcome}
              </span>
              <span className="text-xs text-white">
                {ev.recordsDelivered}/{ev.recordsAttempted} delivered
              </span>
              {ev.recordsFailed > 0 && (
                <span className="text-xs" style={{ color: '#f43f5e' }}>{ev.recordsFailed} failed</span>
              )}
              {ev.httpStatusCode && (
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>HTTP {ev.httpStatusCode}</span>
              )}
              {ev.durationMs != null && (
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{ev.durationMs}ms</span>
              )}
              <span className="ml-auto text-[11px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {new Date(ev.createdAt).toLocaleString()}
              </span>
              {ev.errorMessage && (
                <p className="w-full text-[11px]" style={{ color: '#f43f5e' }}>{ev.errorMessage}</p>
              )}
            </div>
          );
        })}
      </div>

      {(history.data.totalPages ?? 0) > 1 && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Page {(history.data.number ?? 0) + 1} of {history.data.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-lg px-2.5 py-1 text-[11px] font-medium disabled:opacity-30"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (history.data.totalPages ?? 1) - 1}
              className="rounded-lg px-2.5 py-1 text-[11px] font-medium disabled:opacity-30"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationCard({
  integration,
  canManage,
  onEdit,
  onDelete,
  onToggle,
}: {
  integration: ConnectIntegration;
  canManage: boolean;
  onEdit: (i: ConnectIntegration) => void;
  onDelete: (i: ConnectIntegration) => void;
  onToggle: (i: ConnectIntegration) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const color = INTEGRATION_COLORS[integration.integrationType] ?? '#60a5fa';
  const statusCfg = (STATUS_CONFIG[integration.status] ?? STATUS_CONFIG.ACTIVE)!;

  return (
    <div className="rounded-2xl transition-all" style={{ ...DARK_GLASS, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
      <button className="flex w-full items-center gap-4 px-5 py-4 text-left" onClick={() => setExpanded((v) => !v)}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${color}18`, border: `1px solid ${color}35` }}>
          <span style={{ color }}>{INTEGRATION_ICONS[integration.integrationType] ?? <Plug className="h-5 w-5" />}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">{integration.displayName}</span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${color}15`, color }}>
              {integration.integrationType.replace('_', ' ')}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
            <span>{integration.totalEventsSynced.toLocaleString()} events synced</span>
            {integration.lastSyncAt && (
              <span>Last sync {new Date(integration.lastSyncAt).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className="flex items-center gap-1 text-xs font-medium" style={{ color: statusCfg.color }}>
            {statusCfg.icon}
            {statusCfg.label}
          </span>
          {integration.consecutiveErrors > 0 && (
            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: 'rgba(244,63,94,0.15)', color: '#f43f5e' }}>
              {integration.consecutiveErrors} err
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.3)' }} />
          ) : (
            <ChevronDown className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.3)' }} />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t px-5 pb-5 pt-4" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[
              { label: 'Endpoint', value: integration.endpointUrl ?? '—' },
              { label: 'HTTP Method', value: integration.httpMethod ?? '—' },
              { label: 'Trigger Event', value: integration.triggerEvent },
              { label: 'Batch Size', value: String(integration.batchSize) },
              { label: 'Matched Only', value: integration.matchedOnly ? 'Yes' : 'No' },
              { label: 'API Key', value: integration.apiKeyHint ?? '—' },
            ].map((item) => (
              <div key={item.label}>
                <p className="mb-0.5 text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {item.label}
                </p>
                <p className={`text-xs font-medium ${item.label === 'Endpoint' ? 'truncate' : ''} text-white`}>
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {/* Field Mappings display */}
          {integration.fieldMappings && Object.keys(integration.fieldMappings).length > 0 && (
            <div className="mb-4">
              <p className="mb-2 text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>Field Mappings</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(integration.fieldMappings).map(([k, v]) => (
                  <span key={k} className="rounded-lg px-2 py-1 font-mono text-[11px]" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}>
                    {k} → {v}
                  </span>
                ))}
              </div>
            </div>
          )}

          {integration.lastErrorMessage && (
            <div className="mb-4 rounded-xl px-3 py-2.5 text-xs" style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.2)', color: '#f43f5e' }}>
              <span className="font-semibold">Last error:</span> {integration.lastErrorMessage}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {canManage && (
              <>
                <button
                  onClick={() => onEdit(integration)}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                  style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => onToggle(integration)}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                  style={{
                    background: integration.status === 'PAUSED' ? 'rgba(52,211,153,0.1)' : 'rgba(251,146,60,0.1)',
                    color: integration.status === 'PAUSED' ? '#34d399' : '#fb923c',
                    border: `1px solid ${integration.status === 'PAUSED' ? 'rgba(52,211,153,0.25)' : 'rgba(251,146,60,0.25)'}`,
                  }}
                >
                  <PauseCircle className="h-3.5 w-3.5" />
                  {integration.status === 'PAUSED' ? 'Activate' : 'Pause'}
                </button>
                <button
                  onClick={() => onDelete(integration)}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                  style={{ background: 'rgba(244,63,94,0.08)', color: '#f43f5e', border: '1px solid rgba(244,63,94,0.2)' }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )}

            <button
              onClick={() => setShowSyncHistory((v) => !v)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
              style={{ background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}
            >
              <History className="h-3.5 w-3.5" />
              {showSyncHistory ? 'Hide History' : 'Sync History'}
            </button>
          </div>

          {showSyncHistory && (
            <div className="mt-4 rounded-xl p-3" style={{ background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.12)' }}>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>
                Sync Event History
              </p>
              <SyncHistorySection integrationId={integration.id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function deriveJourneyType(p: CustomerIdentityProfile): string {
  if (p.failedVerifications === 0) return 'Active';
  if (p.successfulVerifications === 0) return 'Stuck+';
  return 'Stuck';
}

function deriveCurrentState(p: CustomerIdentityProfile): string {
  if (p.lastVerificationOutcome === 'MATCH') return 'Recovered';
  if (p.lastVerificationOutcome === 'NOT_MATCHED') return 'Unresolved';
  return 'New';
}

function deriveInsight(p: CustomerIdentityProfile): string {
  if (p.failedVerifications === 0) return 'No failed attempts on record.';
  if (p.successfulVerifications === 0)
    return `${p.failedVerifications} failed attempt${p.failedVerifications !== 1 ? 's' : ''}, still unresolved.`;
  if (p.lastVerificationOutcome === 'MATCH')
    return `Recovered after ${p.failedVerifications} failed attempt${p.failedVerifications !== 1 ? 's' : ''}.`;
  return 'Recovered previously; recent attempt failed.';
}

function CustomerIdentity360({ page, onPageChange }: { page: number; onPageChange: (p: number) => void }) {
  const profiles = useCustomerProfiles(page);

  return (
    <div className="rounded-2xl p-6" style={{ ...DARK_GLASS, boxShadow: '0 4px 32px rgba(0,0,0,0.35)' }}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)' }}>
            <Users className="h-4 w-4" style={{ color: '#34d399' }} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Customer Identity 360</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {profiles.data?.totalElements.toLocaleString() ?? '—'} identity profiles · mobile always masked
            </p>
          </div>
        </div>
      </div>

      {profiles.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Activity className="h-5 w-5 animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} />
        </div>
      ) : !profiles.data?.content.length ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2">
          <Users className="h-8 w-8" style={{ color: 'rgba(52,211,153,0.25)' }} />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
            No identity profiles yet. Profiles are built automatically from verification events.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            {/* Table header */}
            <div className="mb-2 grid min-w-[860px] gap-3 px-3" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr' }}>
              {['Mobile', 'Operator', 'Country', 'Verif.', 'Match %', 'Journey', 'State', 'Insight'].map((h) => (
                <p key={h} className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>{h}</p>
              ))}
            </div>

            <div className="flex flex-col gap-1.5">
              {profiles.data.content.map((profile) => {
                const matchRate = profile.totalVerifications > 0
                  ? Math.round((profile.successfulVerifications / profile.totalVerifications) * 100)
                  : 0;
                const journeyType = deriveJourneyType(profile);
                const currentState = deriveCurrentState(profile);
                const insight = deriveInsight(profile);
                const journeyColor = journeyType === 'Stuck+' ? '#fb923c' : journeyType === 'Stuck' ? '#34d399' : '#60a5fa';
                const stateColor = currentState === 'Recovered' ? '#34d399' : currentState === 'Unresolved' ? '#f43f5e' : 'rgba(255,255,255,0.4)';

                return (
                  <div
                    key={profile.profileId}
                    className="grid min-w-[860px] items-center gap-3 rounded-xl px-3 py-3"
                    style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    {/* Mobile — always fully masked */}
                    <span className="font-mono text-sm font-medium tracking-widest text-white">
                      ************
                    </span>

                    {/* Operator */}
                    <span className="text-xs text-white">{profile.operator ?? '—'}</span>

                    {/* Country */}
                    <span className="text-xs text-white">{profile.countryCode ?? '—'}</span>

                    {/* Total verifications */}
                    <span className="text-xs font-medium" style={{ color: '#60a5fa' }}>
                      {profile.totalVerifications.toLocaleString()}
                    </span>

                    {/* Match rate */}
                    <div className="flex items-center gap-1.5">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${matchRate}%`, background: matchRate >= 80 ? '#34d399' : matchRate >= 50 ? '#fb923c' : '#f43f5e' }}
                        />
                      </div>
                      <span className="shrink-0 text-xs font-medium text-white">{matchRate}%</span>
                    </div>

                    {/* Journey type */}
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{ background: `${journeyColor}15`, color: journeyColor }}
                    >
                      {journeyType}
                    </span>

                    {/* Current state */}
                    <span className="text-xs font-semibold" style={{ color: stateColor }}>
                      {currentState}
                    </span>

                    {/* Insight */}
                    <span className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {insight}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {(profiles.data.totalPages ?? 0) > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Page {(profiles.data.number ?? 0) + 1} of {profiles.data.totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onPageChange(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-30"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
                >
                  Previous
                </button>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= (profiles.data.totalPages ?? 1) - 1}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-30"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

type ConnectTab = 'integrations' | 'import';

const TAB_CONFIG: { id: ConnectTab; label: string; icon: React.ReactNode }[] = [
  { id: 'integrations', label: 'Integrations',  icon: <Link2 className="h-4 w-4" /> },
  { id: 'import',       label: 'Import Data',   icon: <Upload className="h-4 w-4" /> },
];

export function ClientConnectPage() {
  const { hasAnyRole } = useAuth();
  const queryClient = useQueryClient();

  const canManage = hasAnyRole(['AUTHMATECH_CLIENT_ADMIN', 'AUTHMATECH_CLIENT_OPERATOR']);

  const [activeTab, setActiveTab] = useState<ConnectTab>('integrations');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<ConnectIntegration | null>(null);
  const [profilesPage, setProfilesPage] = useState(0);

  const overview = useConnectOverview();
  const integrations = useConnectIntegrations();

  const stuckOverviewQuery = useQuery<StuckPlusOverview>({
    queryKey: ['stuckplus-overview'],
    queryFn: async () => unwrap(await api.get<ResponseEnvelope<StuckPlusOverview>>(Endpoints.me.stuckPlus.overview)),
    staleTime: 60_000,
    retry: 0,
  });
  const stuckOv = stuckOverviewQuery.data;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.delete<ResponseEnvelope<void>>(Endpoints.me.connect.integration(id))),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connect-integrations'] });
      void queryClient.invalidateQueries({ queryKey: ['connect-overview'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      unwrap(await api.patch<ResponseEnvelope<ConnectIntegration>>(Endpoints.me.connect.integration(id), { status })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['connect-integrations'] }),
  });

  if (overview.isLoading && !overview.data) return <PageSpinner />;

  const ov = overview.data;

  const handleSaved = () => {
    setShowForm(false);
    setEditTarget(null);
    void queryClient.invalidateQueries({ queryKey: ['connect-integrations'] });
    void queryClient.invalidateQueries({ queryKey: ['connect-overview'] });
  };

  return (
    <div className="flex min-h-screen flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Authmatech Connect"
          subtitle="CRM Identity Intelligence & Synchronization"
          badge={<Link2 className="h-5 w-5" style={{ color: '#60a5fa' }} />}
        />
        {canManage && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', boxShadow: '0 4px 16px rgba(59,130,246,0.3)' }}
          >
            <Plus className="h-4 w-4" />
            New Integration
          </button>
        )}
      </div>

      <ProductIntro
        dark
        icon={Link2}
        tone="connect"
        eyebrow="Customer Journey Recovery · Connect"
        title="Put Authmatech identity intelligence inside the tools your teams already use."
        points={['One customer view', 'Masked & tenant-isolated', 'Sync is free', 'On-demand enrichment']}
      >
        <p>
          <strong>Authmatech Connect</strong> pushes verification outcomes, recovery journeys and identity
          profiles into your CRM, CDP or support desk — so marketing, support, fraud and ops all see the same
          customer, without exporting a CSV or opening another dashboard.
        </p>
        <p>
          The sync itself is included at no credit cost; on-demand <strong>Identity Enrichment</strong> adds extra
          mobile-network intelligence to a specific profile when you need it. Only masked identities sync, every
          integration is tenant-isolated, and destination keys are encrypted at rest.
        </p>
      </ProductIntro>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
        {[
          { label: 'Total',          value: ov?.totalIntegrations ?? 0,  color: '#60a5fa' },
          { label: 'Active',         value: ov?.activeIntegrations ?? 0,  color: '#34d399' },
          { label: 'Paused',         value: ov?.pausedIntegrations ?? 0,  color: '#fb923c' },
          { label: 'Errored',        value: ov?.erroredIntegrations ?? 0, color: '#f43f5e' },
          { label: 'Events Synced',  value: ov?.totalEventsSynced ?? 0,   color: '#a78bfa' },
          { label: 'Successful',     value: ov?.successfulSyncs ?? 0,     color: '#34d399' },
          { label: 'Failed Syncs',   value: ov?.failedSyncs ?? 0,         color: '#fb7185' },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-2xl p-4"
            style={{ ...DARK_GLASS, boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}
          >
            <p className="mb-1.5 text-[10px] uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {kpi.label}
            </p>
            <p className="text-xl font-bold" style={{ color: kpi.color }}>
              {kpi.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* CRM Identity Intelligence KPIs */}
      {(ov || stuckOv) && (
        <div className="rounded-2xl px-5 py-4" style={{ background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.12)' }}>
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#60a5fa' }}>
            CRM Identity Intelligence
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              { label: 'Events Synced',      value: ov?.totalEventsSynced ?? 0,  color: '#60a5fa' },
              { label: 'Successful Syncs',   value: ov?.successfulSyncs ?? 0,    color: '#34d399' },
              { label: 'Recovered Users',    value: stuckOv?.stuckCount ?? '—',  color: '#34d399' },
              { label: 'Unresolved Users',   value: stuckOv?.stuckPlusCount ?? '—', color: '#fb923c' },
              {
                label: 'CRM Match Rate',
                value: ov && ov.totalEventsSynced > 0
                  ? `${((ov.successfulSyncs / ov.totalEventsSynced) * 100).toFixed(1)}%`
                  : '—',
                color: '#a78bfa',
              },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl px-3 py-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="mb-1 text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>{kpi.label}</p>
                <p className="text-lg font-bold" style={{ color: kpi.color }}>
                  {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Privacy notice */}
      <div className="flex items-start gap-3 rounded-2xl px-5 py-4" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" style={{ color: '#60a5fa' }} />
        <div>
          <p className="text-sm font-semibold text-white">Privacy by design</p>
          <p className="mt-0.5 text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            All data synced through Connect uses masked identifiers (<span className="font-mono font-bold text-white">************</span>).
            Full mobile number reveal is exclusively available through Stuck+ Secure Reveal, which requires credits and admin authorization.
            API credentials are encrypted at rest and never returned after save.
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 0 }}>
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px',
              fontSize: 13.5, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? '#60a5fa' : 'rgba(255,255,255,0.45)',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === tab.id ? '#60a5fa' : 'transparent'}`,
              marginBottom: -1,
              transition: 'all 0.15s',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Import Data tab */}
      {activeTab === 'import' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <ImportWizard canManage={canManage} />
          <div style={{
            background: 'linear-gradient(135deg, #050e1d 0%, #060f22 100%)',
            border: '1px solid rgba(96,165,250,0.10)',
            borderRadius: 16, padding: '24px 28px',
          }}>
            <ImportHistorySection />
          </div>
        </div>
      )}

      {/* Integrations tab */}
      {activeTab === 'integrations' && <>
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Integrations</h2>
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {integrations.data?.length ?? 0} configured
          </span>
        </div>

        {integrations.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Activity className="h-5 w-5 animate-spin" style={{ color: 'rgba(255,255,255,0.3)' }} />
          </div>
        ) : !integrations.data?.length ? (
          <div
            className="flex flex-col items-center justify-center gap-4 rounded-2xl py-16"
            style={{ ...DARK_GLASS, boxShadow: '0 4px 32px rgba(0,0,0,0.35)' }}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)' }}>
              <Link2 className="h-7 w-7" style={{ color: '#60a5fa' }} />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-white">No integrations yet</p>
              <p className="mt-1 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Connect your CRM, webhook, or custom API to sync verification events.
              </p>
            </div>
            {canManage && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
              >
                <Plus className="h-4 w-4" />
                Create First Integration
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {integrations.data.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                canManage={canManage}
                onEdit={setEditTarget}
                onDelete={(i) => {
                  if (window.confirm(`Delete integration "${i.displayName}"? This action cannot be undone.`)) {
                    deleteMutation.mutate(i.id);
                  }
                }}
                onToggle={(i) =>
                  toggleMutation.mutate({
                    id: i.id,
                    status: i.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED',
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Customer Identity 360 */}
      <CustomerIdentity360 page={profilesPage} onPageChange={setProfilesPage} />

      {/* Coming soon integrations */}
      <div className="rounded-2xl p-6" style={{ ...DARK_GLASS, boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
        <h3 className="mb-4 text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Coming Soon
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {['Salesforce', 'HubSpot', 'Zoho CRM', 'Dynamics 365', 'Shopify', 'Zendesk'].map((name) => (
            <div
              key={name}
              className="flex flex-col items-center justify-center gap-2 rounded-xl py-4 text-center"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <Globe className="h-6 w-6" style={{ color: 'rgba(255,255,255,0.2)' }} />
              <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.3)' }}>{name}</span>
            </div>
          ))}
        </div>
      </div>

      </>}

      {(showForm || editTarget) && (
        <IntegrationFormModal
          existing={editTarget ?? undefined}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
